import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import { consultasStockApi } from '../api/consultasStock'
import useStockPresea from '../hooks/useStockPresea'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtFechaHora } from '../utils/fecha'

// Consulta de stock a depósito dentro de la NV (Martin, 21 oct 2026):
// el botón "🏬 Pedir stock a depósito" abre el modal con los renglones de la
// NV; la respuesta de depósito vuelve a la NV en la columna "Depósito".
// Una sola pieza para el form de edición y el detalle de la NV.

const num = (n) => (n === null || n === undefined ? '' : Number(n).toLocaleString('es-AR', { maximumFractionDigits: 2 }))

/** Las consultas de la NV y la última respuesta por artículo. */
export function useConsultasDeposito(nvId) {
  const [consultas, setConsultas] = useState([])
  const recargar = useCallback(async () => {
    if (!nvId) return
    try {
      const { data } = await consultasStockApi.deNv(nvId)
      setConsultas(data.consultas || [])
    } catch { /* la NV se usa igual sin esta columna */ }
  }, [nvId])
  useEffect(() => { recargar() }, [recargar])

  // La consulta más nueva manda (vienen ordenadas de la más nueva a la más vieja).
  const porArticulo = useMemo(() => {
    const m = {}
    for (const c of consultas) {
      for (const it of c.items) {
        if (!m[it.articulo_id] && it.estado !== 'cancelado') m[it.articulo_id] = { ...it, consulta_id: c.id, consulta_estado: c.estado }
      }
    }
    return m
  }, [consultas])

  return { consultas, porArticulo, recargar, hay: consultas.length > 0 }
}

/** Badge + sub-línea con lo que respondió depósito para un renglón. */
export function RespuestaDeposito({ item }) {
  if (!item) return <span style={{ color: colors.textMeta }}>—</span>
  let badge
  if (item.estado === 'respondido') {
    badge = item.respuesta === 'sin_stock'
      ? <span style={{ ...s.badge, background: colors.errBg, color: colors.err }}>Sin stock</span>
      : <span style={{ ...s.badge, background: colors.okBg, color: colors.ok }}>Disponible{item.cantidad_disponible !== null ? ` · ${num(item.cantidad_disponible)}` : ''}</span>
  } else if (item.estado === 'vencido') {
    badge = <span style={{ ...s.badge, background: colors.bgSoft, color: colors.textSec }}>Vencida</span>
  } else {
    badge = <span style={{ ...s.badge, background: colors.warnBg, color: colors.warn }}>Pedido a depósito</span>
  }

  let sub = null
  if (item.estado === 'respondido') {
    sub = item.resolucion === 'ya_sin_stock'
      ? `marcado en LGR${item.respondido_at ? ' ' + fmtFechaHora(item.respondido_at) : ''}`
      : `${item.respondido_por || 'Depósito'} · ${fmtFechaHora(item.respondido_at)}${item.resolucion === 'respuesta_reciente' ? ' (respuesta reciente)' : ''}`
  } else if (item.estado === 'vencido') {
    sub = 'sin respuesta en 24 h'
  } else if (item.vence_at) {
    sub = `vence ${fmtFechaHora(item.vence_at)}`
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 140 }}>
      <span>{badge}</span>
      {sub && <span style={s.sub}>{sub}</span>}
      {item.observacion && <span style={s.sub}>“{item.observacion}”</span>}
    </div>
  )
}

const RESOLUCION = {
  consultado: 'Enviado a depósito por Telegram.',
  suscripto: 'Otro vendedor ya lo preguntó: te llega la misma respuesta.',
  respuesta_reciente: 'Respondido en las últimas 24 h.',
  ya_sin_stock: 'Ya figura sin stock en LGR: no se consulta.',
}
const VERIFICAR = 'Figura sin stock en LGR: va a depósito para verificar el estante. Si lo encuentran, vuelve a estar en stock.'

/**
 * Modal "Pedir stock a depósito". `renglones`: [{articulo_id, codigo_alfa,
 * descripcion, cantidad}] de la NV. Trae el stock de Presea al abrir (en vivo,
 * referencia) y lo manda con la consulta.
 */
export function PedirStockDepositoModal({ nvId, renglones, onClose, onCreada }) {
  const stock = useStockPresea()
  const deArticulo = renglones.filter(r => r.articulo_id)
  const [elegidos, setElegidos] = useState(() => new Set(deArticulo.map(r => r.articulo_id)))
  const [observacion, setObservacion] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null)
  // #124: los que figuran sin stock en LGR → «¿Consultar igual?» por renglón.
  const [sinStock, setSinStock] = useState({})       // {articulo_id: {sin_stock_at, sin_stock_por}}
  const [decision, setDecision] = useState({})       // {articulo_id: 'consultar' | 'no'}

  useEffect(() => {
    stock.consultar(deArticulo.map(r => r.codigo_alfa))
    consultasStockApi.sinStock(deArticulo.map(r => r.articulo_id))
      .then(({ data }) => setSinStock(Object.fromEntries((data.articulos || []).map(a => [a.articulo_id ?? a.id, a]))))
      .catch(() => { /* sin el dato, el servidor resuelve como siempre: no se consulta */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sinDecidir = deArticulo.filter(r => elegidos.has(r.articulo_id) && sinStock[r.articulo_id] && !decision[r.articulo_id])

  const alternar = (id) => setElegidos(prev => {
    const n = new Set(prev)
    n.has(id) ? n.delete(id) : n.add(id)
    return n
  })

  const enviar = async () => {
    if (sinDecidir.length) {
      setError('Hay productos que figuran sin stock en LGR: elegí si los consultás igual al depósito o no.')
      return
    }
    setEnviando(true)
    setError(null)
    try {
      const items = deArticulo.filter(r => elegidos.has(r.articulo_id)).map(r => ({
        articulo_id: r.articulo_id,
        cantidad: r.cantidad === '' || r.cantidad === null ? null : Number(r.cantidad),
        stock_presea: stock.porCodigo?.[r.codigo_alfa] ?? null,
        consultar_igual: !!sinStock[r.articulo_id] && decision[r.articulo_id] === 'consultar',
      }))
      const { data } = await consultasStockApi.crear(nvId, { items, observacion: observacion || null })
      setResultado(data.consulta)
      onCreada?.(data.consulta)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo enviar la consulta.')
    } finally {
      setEnviando(false)
    }
  }

  const deps = stock.depositos.length ? stock.depositos : ['100', '400', '401']

  if (resultado) {
    return (
      <Modal title={`Consulta de stock #${resultado.id}`} onClose={onClose} maxWidth="640px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
          {resultado.items.map(it => (
            <div key={it.id} style={s.resFila}>
              <RespuestaDeposito item={it} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{it.descripcion}</div>
                <div style={s.sub}>{it.verificar_sin_stock && it.resolucion !== 'ya_sin_stock' ? VERIFICAR : RESOLUCION[it.resolucion]}</div>
              </div>
            </div>
          ))}
          <p style={{ ...s.sub, margin: 0 }}>Cuando depósito responda, la respuesta aparece en la NV y te llega un aviso.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Boton variante="primary" onClick={onClose}>Listo</Boton>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Pedir stock a depósito" onClose={onClose} maxWidth="680px">
      <p style={{ ...s.sub, margin: `0 0 ${space.md}px` }}>Tildá los productos de la NV que querés que depósito verifique. El stock de Presea es solo referencia.</p>
      {stock.error && <div style={s.error}>{stock.error} La consulta sale igual, sin ese dato.</div>}
      <div style={{ overflowX: 'auto' }}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 28 }}></th>
              <th style={s.th}>Producto</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Pide</th>
              {deps.map(d => <th key={d} style={{ ...s.th, textAlign: 'right' }}>{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {deArticulo.map(r => {
              const st = stock.porCodigo?.[r.codigo_alfa]
              const falta = st && Number(r.cantidad) > (st.disponible ?? 0)
              const marca = sinStock[r.articulo_id]
              const preguntar = marca && elegidos.has(r.articulo_id)
              return (
                <Fragment key={r.articulo_id}>
                <tr style={{ borderBottom: preguntar ? 'none' : `1px solid ${colors.borderSoft}`, cursor: 'pointer' }} onClick={() => alternar(r.articulo_id)}>
                  <td style={s.td}><input type="checkbox" checked={elegidos.has(r.articulo_id)} onChange={() => alternar(r.articulo_id)} onClick={e => e.stopPropagation()} /></td>
                  <td style={s.td}>
                    <div>{r.descripcion}</div>
                    <div style={{ ...s.sub, ...sx.mono }}>{r.codigo_alfa}</div>
                  </td>
                  <td style={{ ...s.td, ...sx.mono, textAlign: 'right' }}>{num(r.cantidad)}</td>
                  {deps.map(d => (
                    <td key={d} style={{ ...s.td, ...sx.mono, textAlign: 'right', color: falta ? colors.errStrong : colors.text, fontWeight: falta ? 700 : 400 }}>
                      {stock.cargando ? '…' : st === undefined || st === null ? '—' : num(st.depositos?.[d] ?? 0)}
                    </td>
                  ))}
                </tr>
                {preguntar && (
                  <tr style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                    <td style={s.td}></td>
                    <td colSpan={2 + deps.length} style={{ ...s.td, paddingTop: 0 }}>
                      <div style={s.pregunta}>
                        <span style={{ flex: 1, minWidth: 220 }}>
                          Este producto figura sin stock en LGR{marca.sin_stock_at ? ` desde ${fmtFechaHora(marca.sin_stock_at)}` : ''}{marca.sin_stock_por ? ` (${marca.sin_stock_por})` : ''}. ¿Querés consultarlo igual al depósito?
                        </span>
                        <span style={{ display: 'flex', gap: space.sm }}>
                          <Boton sm variante={decision[r.articulo_id] === 'consultar' ? 'primary' : 'secondary'}
                            onClick={() => setDecision(dd => ({ ...dd, [r.articulo_id]: 'consultar' }))}>Consultar igual</Boton>
                          <Boton sm variante={decision[r.articulo_id] === 'no' ? 'primary' : 'secondary'}
                            onClick={() => setDecision(dd => ({ ...dd, [r.articulo_id]: 'no' }))}>No consultar</Boton>
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      <textarea
        id="consulta-stock-observacion"
        style={s.textarea}
        placeholder="Observación para depósito (opcional): es para hoy, cliente en el local…"
        value={observacion}
        maxLength={500}
        onChange={e => setObservacion(e.target.value)}
      />
      {error && <div style={s.error}>{error}</div>}
      {sinDecidir.length > 0 && (
        <div style={{ ...s.sub, marginTop: space.md, textAlign: 'right' }}>
          Elegí «Consultar igual» o «No consultar» en {sinDecidir.length === 1 ? 'el producto que figura' : `los ${sinDecidir.length} productos que figuran`} sin stock.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.lg }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" onClick={enviar} disabled={enviando || elegidos.size === 0 || sinDecidir.length > 0}>
          {enviando ? 'Enviando…' : `Pedir a depósito (${elegidos.size})`}
        </Boton>
      </div>
    </Modal>
  )
}

/** Card del detalle de la NV: qué se le preguntó a depósito y qué respondió. */
export function ConsultasDepositoCard({ consultas, onCancelar }) {
  if (!consultas.length) return null
  const ESTADO = {
    pendiente: { t: 'Pendiente', bg: colors.warnBg, c: colors.warn },
    respondida_parcial: { t: 'Respondida parcial', bg: colors.warnBg, c: colors.warn },
    respondida: { t: 'Respondida', bg: colors.okBg, c: colors.ok },
    vencida: { t: 'Vencida', bg: colors.errBg, c: colors.err },
    cancelada: { t: 'Cancelada', bg: colors.bgSoft, c: colors.textSec },
  }
  return (
    <div style={{ ...sx.card, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: space.lg }}>
      <h3 style={{ margin: 0, fontSize: text.title }}>🏬 Stock pedido a depósito</h3>
      {consultas.map(c => {
        const e = ESTADO[c.estado] || ESTADO.pendiente
        return (
          <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
              <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }}>#{c.id} · {fmtFechaHora(c.created_at)}</span>
              <span style={{ ...s.badge, background: e.bg, color: e.c }}>{e.t}</span>
              {onCancelar && ['pendiente', 'respondida_parcial'].includes(c.estado) && (
                <Boton variante="ghost" sm onClick={() => onCancelar(c)}>Cancelar consulta</Boton>
              )}
            </div>
            {c.items.map(it => (
              <div key={it.id} style={s.resFila}>
                <RespuestaDeposito item={it} />
                <div style={{ minWidth: 0 }}>
                  <div>{it.descripcion}</div>
                  <div style={s.sub}><span style={sx.mono}>{it.codigo_alfa}</span>{it.cantidad !== null && <> · pide <span style={sx.mono}>{num(it.cantidad)}</span></>}</div>
                </div>
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

const s = {
  badge: { ...sx.badge, whiteSpace: 'nowrap' },
  th: { ...sx.th, padding: `${space.sm}px ${space.md}px` },
  sub: { fontSize: text.sec, color: colors.textMeta },
  td: { padding: space.md, fontSize: text.body, color: colors.text, verticalAlign: 'middle' },
  pregunta: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap', background: colors.warnBg, color: colors.warn, borderRadius: radius.sm, padding: `${space.sm}px ${space.md}px`, fontSize: text.sec },
  resFila: { display: 'grid', gridTemplateColumns: '170px minmax(0, 1fr)', gap: space.lg, alignItems: 'start', fontSize: text.body },
  textarea: {
    width: '100%', boxSizing: 'border-box', marginTop: space.lg, minHeight: 56, padding: `${space.md}px ${space.lg}px`,
    border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontFamily: font.sans, fontSize: text.body, color: colors.text, resize: 'vertical',
  },
  error: { background: colors.errBg, color: colors.err, border: `1px solid ${colors.errBorder}`, borderRadius: radius.sm, padding: `${space.md}px ${space.lg}px`, fontSize: text.sec, marginTop: space.md },
}
